import moment from 'moment';
import { ref, computed } from 'vue';
import { cloneDeep } from 'lodash';
import i18n from '@/i18n';
import { numberOfDownloadFunc } from '@/containers/performance/performanceUtil';
import {
  downloadMetricDataImage as downloadImage,
  downloadMetricDataExcel as downloadExcel,
  downloadRawMetricDataExcel as downloadRawExcel
} from '@/containers/performance/productMetricLib.js';
import {
  isUnitAbleToConvert,
  convertToHigherUnit,
  convertNumberSymbol,
  convertToSpecificUnit,
  convertToSpecificSymbol
} from '@/utils/definition.js';

import { useDateTimeFormat } from '@/composables/useDateTimeFormat';

const { fullTimeFormat } = useDateTimeFormat();

const useState = (state) => state.value;

export const DEFAULT_STATISTIC = null;

const STATISTICS = {
  MAX: 'MAX',
  MIN: 'MIN',
  AVG: 'AVG',
  SUM: 'SUM'
};

export const getDefaultMetricPayload = (productType, start, end, collInterval) => {
  const { getInterval } = useInterval();
  const duration = moment(end).valueOf() - moment(start).valueOf();
  const interval = getInterval(duration);
  const { defaultValue } = getDefaultStatType(productType);
  const isCollInterval = parseInt(interval) <= parseInt(collInterval);
  const product = productType.toLowerCase();
  if (product === 'cdn') {
    return {
      statisticsTypeList: DEFAULT_STATISTIC,
      interval
    };
  }
  return {
    statisticsTypeList: isCollInterval ? DEFAULT_STATISTIC : [defaultValue],
    interval
  };
};
export const useInterval = () => {
  const interval = ref(null);
  const minisecond = 60 * 60 * 1000;
  const oneMinute = 60 * 1000;
  const intervalOptions = ref([
    {
      key: '1m',
      value: oneMinute,
      text: '1 ' + i18n.global.t('label.onlyMin'),
      disabled: false,
      breakpoint: 24 * minisecond
    },
    {
      key: '5m',
      value: 5 * oneMinute,
      text: '5 ' + i18n.global.t('label.minute'),
      disabled: false,
      breakpoint: 144 * minisecond
    },
    {
      key: '10m',
      value: 10 * oneMinute,
      text: '10 ' + i18n.global.t('label.minute'),
      disabled: false,
      breakpoint: 312 * minisecond
    },
    {
      key: '30m',
      value: 30 * oneMinute,
      text: '30 ' + i18n.global.t('label.minute'),
      disabled: false,
      breakpoint: Infinity
    }
  ]);
  const setInterval = (queryTime) => {
    for (const el of useState(intervalOptions)) {
      el.disabled = false;
    }
    for (const el of useState(intervalOptions)) {
      if (queryTime <= el.breakpoint) {
        interval.value = el.key;
        break;
      }
      el.disabled = true;
    }
  };

  const getIntevalValue = (intervalKey) => {
    for (const { key, value } of useState(intervalOptions)) {
      if (key === intervalKey) return value;
    }
  };

  const getInterval = (queryTime) => {
    for (const el of useState(intervalOptions)) {
      if (queryTime <= el.breakpoint) return el.key;
    }
  };
  return { interval, intervalOptions, getInterval, setInterval, getIntevalValue };
};

const getDefaultStatType = (productType) => {
  const { MAX } = STATISTICS;
  const product = productType.toLowerCase();
  if (product === 'cdn')
    return {
      defaultValue: DEFAULT_STATISTIC,
      disabledValues: Object.values(STATISTICS)
    };
  return {
    defaultValue: MAX,
    disabledValues: []
  };
};

export const useStatisticalType = (productType = '') => {
  const { MIN, MAX, AVG, SUM } = STATISTICS;
  const statisticalOptions = ref([
    { key: MAX, value: MAX, text: i18n.global.t('label.max'), disabled: false },
    { key: MIN, value: MIN, text: i18n.global.t('label.min'), disabled: false },
    { key: AVG, value: AVG, text: i18n.global.t('label.avg'), disabled: false },
    { key: SUM, value: SUM, text: i18n.global.t('label.sum'), disabled: false }
  ]);

  const { defaultValue: defaultStatType, disabledValues } = getDefaultStatType(productType);

  for (const item of useState(statisticalOptions)) {
    item.disabled = disabledValues.includes(item.value);
  }

  const defaultOption =
    useState(statisticalOptions).find(({ value }) => value === defaultStatType) || {};
  const statisticalType = ref([{ ...defaultOption }]);
  const setStatisticType = (value) => {
    const defaultVal = useState(statisticalOptions).find((el) => el.value == value);
    statisticalType.value = [defaultVal];
  };
  const setDefaultStatType = () => setStatisticType(defaultStatType);

  const isDefaultStatType = computed(() => {
    if (useState(statisticalType).length !== 1) return false;
    return useState(statisticalType)[0]?.key === defaultOption.key;
  });

  return {
    statisticalType,
    statisticalOptions,
    defaultStatType,
    setStatisticType,
    isDefaultStatType,
    setDefaultStatType
  };
};

export const useLastValue = () => {
  const lastSeries = ref([]);
  const lastValueArr = ref([]);
  const updateLastValue = (chartSeries) => {
    lastValueArr.value = [];
    lastSeries.value = [];
    const chartSeriesClone = cloneDeep(chartSeries);
    lastSeries.value = chartSeriesClone.map((series) => {
      if (!series.data || !series.data.length) return series;
      const lastIndex = series.data.findLastIndex((dt) => dt[1] !== null);
      let lastValue = 0;
      let lastDate = series.data[series.data.length - 1][0];
      if (lastIndex > -1) {
        lastValue = series.data[lastIndex][1];
        lastDate = series.data[lastIndex][0];
        series.data[lastIndex] = {
          value: series.data[lastIndex],
          symbol: 'emptyCircle',
          symbolSize: '7'
        };
      }
      lastValueArr.value.push({
        color: series.itemStyle.color,
        name: series.name,
        shortName: series.name,
        lastValue,
        lastIndex,
        lastDate
      });
      return series;
    });
  };
  return { lastValueArr, lastSeries, updateLastValue };
};
export const useDownloadChart = () => {
  const downloadPNGFile = ({ metric, mainChart, hasData, downloadType }) => {
    const funcDownloadPNG = () => {
      const individual = mainChart.querySelector('.individual-items');
      const actions = mainChart.querySelector('.chart-actions');
      const lastValue = mainChart.querySelector('.last-value');
      if (individual) {
        individual.style.display = 'none';
      }

      if (actions) {
        actions.style.display = 'none';
      }
      if (lastValue) {
        lastValue.style.display = 'none';
      }

      mainChart.style.border = 'none';
      mainChart.style.boxShadow = 'unset';
      downloadImage(metric, mainChart);
      mainChart.style.border = '1px solid #e3e3e3';
      if (individual) {
        individual.style.display = 'block';
      }

      if (actions) {
        actions.style.display = 'flex';
      }
      if (lastValue) {
        lastValue.style.display = 'flex';
      }
    };

    callPopupDownload(funcDownloadPNG, hasData, downloadType);
  };

  const downloadRawExcelFile = ({ paramDownloadChart, dateOption, hasData, downloadType }) => {
    const { checkDownloadLimited, numberOfDownload, numberOfLimited } = numberOfDownloadFunc(
      dateOption,
      paramDownloadChart.countSelectedObject,
      paramDownloadChart.countSelectedTargets
    );

    const getRawData = (params, productTypeCode, dateOption) => {
      const [startDate, endDate] = dateOption;
      params = { ...params, rawDataExportYn: 'Y' };
      window.app.$http.product.downloadRawMetricData(params, productTypeCode).then((data) => {
        downloadRawExcel(startDate, endDate, data);
      });
    };

    callPopupDownload(
      () => getRawData(paramDownloadChart.params, paramDownloadChart.productTypeCode, dateOption),
      hasData,
      downloadType,
      checkDownloadLimited,
      numberOfDownload,
      numberOfLimited
    );
  };
  return { downloadPNGFile, downloadRawExcelFile };
};

const callPopupDownload = (
  funcDownload,
  hasData,
  downloadType,
  checkDownloadLimited = false,
  numberOfDownload = 0,
  numberOfLimited = 0
) => {
  if (!hasData || (downloadType === 'excel-raw' && checkDownloadLimited)) {
    window.app.$modal.open({
      rootId: 'app',
      type: 'alert',
      title: i18n.global.t('title.notice'),
      message: function () {
        if (checkDownloadLimited) {
          return `${i18n.global.t('page.performance.downloadCheckRawData', {
            params: `${numberOfDownload}/${numberOfLimited}`
          })}`;
        } else {
          return `${i18n.global.t('message.noDownloadData')}`;
        }
      }.call(window.app),
      action: () => {
        return;
      }
    });
  } else {
    window.app.$modal.open({
      rootId: 'app',
      type: 'confirm',
      title: i18n.global.t('title.notice'),
      message: `<div>` + i18n.global.t('page.performance.downloadCheck') + `</div>`,
      action: () => {
        funcDownload();
      },
      cancel: () => {}
    });
  }
};

const tooltipFormatter = (params, dataUnit) => {
  const dateTime = fullTimeFormat(params[0].axisValue);
  const contents = [];
  for (const { marker, seriesName, value } of params) {
    const [, val] = value;
    if (val === null) continue;
    const displayValue = Number(parseFloat(val).toFixed(2));
    const textValue = displayValue ? [displayValue, dataUnit].join('') : 0;
    contents.push(`
      <div style="display: flex; justify-content: space-between;align-items: center; line-height: 16px">
        <div class="ellipsis">${marker} ${seriesName}</div>
        <div style="padding-left: 8px; font-weight: bold;">${textValue}</div>
      </div>
    `);
  }
  if (!contents.length) return;
  return [`<div>${dateTime}</div>`, ...contents].join('');
};

const roundNumber = (num, roundMethod, decimal) => {
  const powVal = Math.pow(10, decimal);
  const roundedNum = roundMethod(num * powVal);
  return Math.round(roundedNum) / powVal;
};
const roundUp = (num, decimal = 2) => roundNumber(num, Math.ceil, decimal);
const roundDown = (num, decimal = 2) => roundNumber(num, Math.floor, decimal);
const roundDecimal = (num, decimal = 2) => {
  if (!num) return 0;
  return Number(parseFloat(num).toFixed(decimal));
};
const adjustNumber = (num, ratio) => {
  if (num >= 0) return num * (1 + ratio);
  return num * (1 - ratio);
};

export const useCommonChartOptions = (chartOptionsDefault, chartType, metricUnit, displayUnit) => {
  const chartOptions = ref(chartOptionsDefault);
  const dataUnit = ref('');
  const unitConfig = ref({
    isConvertible: false,
    unit: metricUnit,
    highestUnit: ''
  });

  const getXAxis = (gridIndex = 0) => {
    const { xAxis } = useState(chartOptions);
    return xAxis.findIndex((el) => el.gridIndex === gridIndex);
  };
  const setXAxis = (options, gridIndex = 0) => {
    const idx = getXAxis(gridIndex);
    const { xAxis } = useState(chartOptions);
    useState(chartOptions).xAxis[idx] = {
      ...xAxis[idx],
      ...options
    };
  };

  const getYAxis = (gridIndex = 0) => {
    const { yAxis } = useState(chartOptions);
    return yAxis.findIndex((el) => el.gridIndex === gridIndex);
  };
  const setYAxis = (options, gridIndex = 0) => {
    const idx = getYAxis(gridIndex);
    const { yAxis } = useState(chartOptions);
    useState(chartOptions).yAxis[idx] = {
      ...yAxis[idx],
      ...options
    };
  };

  const setGridView = (show, gridIndex) => {
    if (!show) setSeries([], 1);
    useState(chartOptions).grid[gridIndex] = {
      ...useState(chartOptions).grid[gridIndex],
      show
    };
  };

  const setSeries = (chartEl, gridIndex = 0) => {
    const series = chartEl.map((el) => ({
      ...el,
      xAxisIndex: gridIndex,
      yAxisIndex: gridIndex
    }));
    const remainedSeries = useState(chartOptions).series.filter(
      (el) => el.xAxisIndex !== gridIndex
    );
    useState(chartOptions).series = [...remainedSeries, ...series];
  };

  const setEnterable = (chartSeries) => {
    const count = chartSeries.filter((item) => item.data.length > 0).length;
    useState(chartOptions).tooltip.enterable = count > 12;
  };

  const setXaxisLabelFormatrer = (formatter, gridIndex = 0) => {
    const idx = getXAxis(gridIndex);
    useState(chartOptions).xAxis[idx].axisLabel.formatter = formatter;
  };

  const setYaxisLabelFormatrer = (formatter, gridIndex = 0) => {
    const idx = getYAxis(gridIndex);
    useState(chartOptions).yAxis[idx].axisLabel.formatter = formatter;
  };

  const setTooltipFormatter = () => {
    useState(chartOptions).tooltip.formatter = (params) =>
      tooltipFormatter(params, useState(dataUnit));
  };

  const setShowTooltip = (value) => {
    useState(chartOptions).tooltip.formatter = (params) => {
      if (!value) return '';
      return tooltipFormatter(params, useState(dataUnit));
    };
  };

  const stackData = (chartData, keepNull = false) => {
    const stackedValues = {};
    for (const { data } of chartData) {
      for (const [xVal, yVal] of data) {
        if (keepNull) stackedValues[xVal] ??= null;
        if (yVal != null) {
          stackedValues[xVal] = (stackedValues[xVal] || 0) + Number(yVal);
        }
      }
    }
    const result = [];
    for (const prop in stackedValues) {
      result.push([prop, stackedValues[prop]]);
    }
    return result;
  };

  const logScaler = (value) => {
    const sign = value > 0 ? 1 : -1;
    return sign * Math.log(Math.abs(value) + 1);
  };

  const minMaxScaler = (data, minValue, maxValue) => {
    const [minVal, maxVal] = [logScaler(minValue), logScaler(maxValue)];
    const scaleValue = maxVal - minVal || 1;
    const buffer = minVal === maxVal ? 0.5 : 0;
    for (let i = 0; i < data.length; i++) {
      const [, value] = data[i];
      if (value === null) continue;
      const scaledValue = logScaler(value);
      data[i][1] = (scaledValue - minVal + buffer) / scaleValue;
    }
    return data;
  };

  const addNullValue = (timeSeries, interval) => {
    const N = timeSeries.length;
    if (!N) return [];
    const aggSeries = [];
    for (let i = 0; i < N - 1; i++) {
      const [curPoint, nextPoint] = [timeSeries[i], timeSeries[i + 1]];
      aggSeries.push(curPoint);
      const [curTime, nextTime] = [curPoint[0], nextPoint[0]];
      const isGreateThanInterval = nextTime - curTime > interval;
      if (isGreateThanInterval) aggSeries.push([(nextTime + curTime) / 2, null]);
    }
    aggSeries.push(timeSeries[N - 1]);
    return aggSeries;
  };

  const createDummyPoint = ({ x1, y1 }, { x2, y2 }, x) => {
    const m = (y2 - y1) / (x2 - x1);
    const y = y1 + m * (x - x1);
    return [x, y];
  };

  const addBreakpoint = ({
    aggLine,
    replacedPortion,
    displayTime,
    originalInterval,
    displayInterval
  }) => {
    const { startTime, endTime } = displayTime;

    const xAxis = Object.keys(aggLine);
    xAxis.sort();

    const chartData = xAxis.map((timeVal) => [Number(timeVal), aggLine[timeVal]]);

    const [leftSeries, middleSeries, rightSeries] = [[], [], []];
    for (const point of chartData) {
      const [timeVal] = point;
      if (timeVal < startTime) leftSeries.push([...point]);
      else if (timeVal <= endTime) middleSeries.push([...point]);
      else rightSeries.push([...point]);
    }

    if (originalInterval === displayInterval) {
      return addNullValue([...leftSeries, ...middleSeries, ...rightSeries], displayInterval);
    }

    const [leftLen, midLen, rightLen] = [
      leftSeries.length,
      middleSeries.length,
      rightSeries.length
    ];
    const replacedX = Object.keys(replacedPortion);
    replacedX.sort();

    const repLen = replacedX.length;
    const repLeftPoint = repLen ? [replacedX[0], replacedPortion[replacedX[0]]] : rightSeries[0];
    const repRightPoint = repLen
      ? [replacedX[repLen - 1], replacedPortion[replacedX[repLen - 1]]]
      : leftSeries[leftLen - 1];
    //
    if (repLeftPoint && leftLen && aggLine[startTime - 1] === undefined) {
      const [leftTime, leftVal] = leftSeries[leftLen - 1];
      const [repXMin, repYmin] = repLeftPoint;
      const diffTime = repXMin - leftTime;
      if (0 < diffTime && diffTime <= originalInterval)
        leftSeries.push(
          createDummyPoint(
            { x1: leftTime, y1: leftVal },
            { x2: repXMin, y2: repYmin },
            startTime - 1
          )
        );
    }
    if (repRightPoint && rightLen && aggLine[endTime + 1] === undefined) {
      const [rightTime, rightVal] = rightSeries[0];
      const [repXMax, repYMax] = repRightPoint;
      const diffTime = rightTime - repXMax;
      if (0 < diffTime && diffTime <= originalInterval)
        rightSeries.unshift(
          createDummyPoint(
            { x1: repXMax, y1: repYMax },
            { x2: rightTime, y2: rightVal },
            endTime + 1
          )
        );
    }
    //
    const data = {
      leftSeries: addNullValue(leftSeries, originalInterval),
      middleSeries: addNullValue(middleSeries, displayInterval),
      rightSeries: addNullValue(rightSeries, originalInterval)
    };
    // if (data.middleSeries[0] !== startTime) data.middleSeries.unshift([startTime, null]);
    // if (data.middleSeries[midLen - 1] !== endTime) data.middleSeries.push([endTime, null]);
    return [...data.leftSeries, ...data.middleSeries, ...data.rightSeries];
  };

  const maxAggregation = (data, chartType) => {
    const aggLine = {};
    const { type, stack } = chartType;
    const isStackedChart = type === 'line' && stack === 'all';
    const aggData = isStackedChart ? [{ data: stackData(data, true) }] : data;
    let [minVal, maxVal] = [Infinity, -Infinity];
    for (const { data } of aggData) {
      for (const [timeVal, numVal] of data) {
        if (numVal === null) continue;
        const roundedVal = Number(parseFloat(numVal).toFixed(2));
        if (aggLine[timeVal] === undefined) aggLine[timeVal] = roundedVal;
        else aggLine[timeVal] = Math.max(aggLine[timeVal], roundedVal);
        minVal = Math.min(minVal, roundedVal);
        maxVal = Math.max(maxVal, roundedVal);
      }
    }
    return { aggLine, minVal, maxVal };
  };

  const aggregateData = (params) => {
    const { chartData, chartType, displayTime, originalInterval, displayInterval } = params;
    const { startTime, endTime } = displayTime;

    const [combinedData, replacedData] = [[], []];
    for (const { data, mappedTarget } of chartData) {
      const [left, middle, right] = [[], [], []];
      for (const point of mappedTarget) {
        if (point[0] < startTime) left.push(point);
        else if (point[0] < endTime) middle.push(point);
        else right.push(point);
      }
      combinedData.push({ data: [...left, ...data, ...right] });
      replacedData.push({ data: middle });
    }

    const { aggLine, minVal, maxVal } = maxAggregation(combinedData, chartType);
    const { aggLine: replacedPortion } = maxAggregation(replacedData, chartType);

    const aggChartData = addBreakpoint({
      aggLine,
      replacedPortion,
      displayTime,
      originalInterval,
      displayInterval
    });
    return minMaxScaler(aggChartData, minVal, maxVal);
  };

  const getYminMax = (chartData, chartType) => {
    const yAxis = { min: Infinity, max: -Infinity };
    if (!chartData || !chartType) return yAxis;
    const { type, stack } = chartType;
    const stackedData = type === 'line' && stack === 'all' ? stackData(chartData) : [];
    const values = [stackedData, ...chartData.map(({ data }) => data)];
    for (const value of values) {
      for (const [, yVal] of value) {
        if (yVal === null) continue;
        yAxis.min = Math.min(yAxis.min, yVal);
        yAxis.max = Math.max(yAxis.max, yVal);
      }
    }
    if (yAxis.min === Infinity) yAxis.min = 0;
    if (yAxis.max === -Infinity) yAxis.max = 0;
    return yAxis;
  };

  const convertUnit = (chartData, refValue, metricUnit) => {
    if (!chartData.length) return { data: [], highestUnit: metricUnit };
    const isConvertible = isUnitAbleToConvert(metricUnit);
    const { unit: highestUnit } = isConvertible
      ? convertToHigherUnit(refValue, metricUnit)
      : convertNumberSymbol(refValue, metricUnit);
    const convertFn = isConvertible ? convertToSpecificUnit : convertToSpecificSymbol;
    const mapFn = ([xVal, yVal]) => {
      if (yVal === null) return [xVal, yVal];
      const numVal = Number(yVal);
      const { value } = convertFn(numVal, metricUnit, highestUnit);
      return [xVal, Number(value)];
    };
    const data = chartData.map((el) => ({
      ...el,
      mappedTarget: el.mappedTarget?.map(mapFn) || [],
      // leftRemain: el.leftRemain?.map(mapFn) || [],
      // rightRemain: el.rightRemain?.map(mapFn) || [],
      data: el.data.map(mapFn)
    }));
    unitConfig.value = {
      isConvertible,
      highestUnit,
      unit: metricUnit
    };
    return { data, highestUnit, isConvertible };
  };

  const setXAxisMinMax = (min, max, gridIndex = 0) => {
    setXAxis({ min, max }, gridIndex);
    setXAxis({ min, max }, 1);
  };

  const setDefaultYaxis = () => setYAxis({ min: 0, max: 1, interval: 0.5 }, 0);

  const setYAxisMinMax = (chartData) => {
    if (!chartData.length) return;
    const { max: maxRawVal } = getYminMax(chartData, useState(chartType));
    const { data, highestUnit, isConvertible } = convertUnit(chartData, maxRawVal, metricUnit);
    const { min: minVal, max: maxVal } = getYminMax(data, useState(chartType));
    const buffer = minVal === maxVal ? 0.05 : 0;
    const yMin = adjustNumber(minVal, -1 * buffer);
    const yMax = adjustNumber(maxVal, 1 * buffer);
    let numDecimal = 3;
    const yOptions = {};
    if (yMin === 0 && yMax === 0) {
      yOptions.min = 0;
      yOptions.max = 1;
      yOptions.interval = 0.5;
    } else {
      const [roundedMinVal, roundedMaxVal] = [roundDown(yMin), roundUp(yMax)];
      const step = (roundedMaxVal - roundedMinVal) / 2;
      const middleVal = roundDecimal(roundedMinVal + step);
      if (![roundedMinVal, roundedMaxVal].includes(middleVal)) numDecimal = 2;
      yOptions.min = roundedMinVal;
      yOptions.max = roundedMaxVal;
      yOptions.interval = step;
    }
    setYAxis(yOptions, 0);
    if (highestUnit === metricUnit) {
      dataUnit.value = '';
    } else {
      dataUnit.value = isConvertible ? ` ${highestUnit}` : highestUnit;
    }
    setTooltipFormatter();
    yAxisFormatter(highestUnit, numDecimal);
  };

  const setXAxisFormatter = (period = 0) => {
    const aDay = 24 * 60 * 60 * 1000;
    const xLabelFormatter =
      period <= aDay
        ? (value) => moment(value).format('HH:mm')
        : (value) => [moment(value).format('MM-DD'), `${moment(value).format('HH')}h`].join('\n');
    setXaxisLabelFormatrer(xLabelFormatter);
  };

  const yAxisFormatter = (highestUnit, numDecimal) => {
    const yLabelFormatter =
      highestUnit === displayUnit
        ? (value) => parseFloat(value.toFixed(numDecimal))
        : (value) => `${parseFloat(value.toFixed(numDecimal))} ${highestUnit}`;
    setYaxisLabelFormatrer(yLabelFormatter);
  };

  const setLegendDisplay = (value) => {
    useState(chartOptions).legend.show = value;
  };

  const setLegendStatus = (selected) => {
    useState(chartOptions).legend = {
      ...useState(chartOptions).legend,
      selected
    };
  };

  const setActivedLegend = (chartSeries, excludedIds = []) => {
    useState(chartOptions).legend.data = chartSeries
      .filter(({ id }) => !excludedIds.includes(id))
      .map(({ name }) => name);
  };

  const updateChartLegend = (isZoom) => {
    useState(chartOptions).legend = {
      ...useState(chartOptions).legend,
      bottom: isZoom ? 35 : 0
    };
  };

  const updateChartGrid = (isZoom) => {
    useState(chartOptions).grid = {
      ...useState(chartOptions).grid,
      bottom: isZoom ? 60 : 30
    };
  };

  const setEmptyChart = () => {
    useState(chartOptions).series = [];
  };

  const setZoomBar = (value) => {
    useState(chartOptions).dataZoom[1].show = value;
  };
  const setZoomScale = (start, end) => {
    for (const index in useState(chartOptions).dataZoom) {
      useState(chartOptions).dataZoom[index].start = start;
      useState(chartOptions).dataZoom[index].end = end;
    }
  };

  const setAnimation = (value) => {
    useState(chartOptions).animation = value;
  };

  const handleDownloadExcelFile = (chartData, dateOptions) => {
    if (!chartData || !Array.isArray(chartData) || !chartType || !metricUnit) return;

    const { max } = getYminMax(chartData, useState(chartType));
    const { data, highestUnit, isConvertible } = convertUnit(chartData, max, metricUnit);
    const { startTime, endTime } = dateOptions;
    const unit = isConvertible ? highestUnit : metricUnit;

    function getVal(val) {
      const parseNumber = roundDecimal(val);
      if (isConvertible) return parseNumber;
      if (parseNumber) return `${parseNumber}${highestUnit}`;
      return parseNumber?.toString();
    }
    const dataDownload = (data || []).map((el) => {
      return {
        ...el,
        data: (el.data || []).map(([time, val]) => [time, getVal(val)]),
        unit,
        metricName: el.metricName,
        name: el.name
      };
    });

    return { startTime, endTime, dataDownload };
  };
  const downloadExcelChart = ({ data, dateOptions, hasData, downloadType }) => {
    return callPopupDownload(
      () => {
        const dataDownload = handleDownloadExcelFile(data, dateOptions);
        if (dataDownload) {
          downloadExcel(dataDownload.startTime, dataDownload.endTime, dataDownload.dataDownload);
        }
      },
      hasData,
      downloadType
    );
  };
  return {
    setSeries,
    setGridView,
    setLegendDisplay,
    setActivedLegend,
    setYAxisMinMax,
    setDefaultYaxis,
    setXAxisFormatter,
    setXAxisMinMax,
    getYminMax,
    aggregateData,
    convertUnit,
    unitConfig,
    chartOptions,
    setLegendStatus,
    updateChartLegend,
    updateChartGrid,
    setEmptyChart,
    setZoomBar,
    setZoomScale,
    setEnterable,
    setShowTooltip,
    setAnimation,
    downloadExcelChart
  };
};
